// Hardware volume-down completes the current set (Android shell only).
//
// Chalky hands, gloves, the phone on the floor mid-set: one physical button that logs the set and
// starts the rest without looking at the screen. Kashtu is a private sideloaded app, so it can do
// something a Play Store app wouldn't — but taking over a system control is still an imposition,
// which is why this is opt-in, off by default, scoped to the logger, and built to fail open.
//
// Failing open is the important part and it's enforced on the NATIVE side: the capture is a lease
// that expires unless renewed. If this page crashes, reloads, or is simply replaced by a deploy,
// the volume button goes back to being the volume button on its own — nobody is ever left with a
// dead key and no way to fix it.

const KEY = "wt_volume_key"; // per-device, like the other logger hardware preferences
const RENEW_MS = 30000;      // comfortably inside the native 90s lease

function plugin() {
  return (typeof window !== "undefined" && window.Capacitor?.Plugins?.VolumeKeys) || null;
}

/** True only in an Android shell built with the plugin. */
export function available() {
  return !!plugin();
}

export function volumeKeyEnabled() {
  try { return localStorage.getItem(KEY) === "1"; } catch { return false; }
}
export function setVolumeKeyEnabled(on) {
  try { localStorage.setItem(KEY, on ? "1" : "0"); } catch {}
}

/**
 * Capture volume-down and call `onPress` for each press. Returns a teardown function.
 * Safe to call when unsupported — you get a no-op teardown and nothing is captured.
 */
export async function captureVolumeKeys(onPress) {
  const p = plugin();
  if (!p) return () => {};
  let stopped = false;
  let handle = null;

  const renew = () => { if (!stopped) void p.setCapture({ on: true }).catch(() => {}); };

  try {
    handle = await p.addListener("volumeKey", () => { if (!stopped) onPress(); });
  } catch {
    return () => {};
  }
  renew();
  const timer = setInterval(renew, RENEW_MS);
  // The native side drops the lease when the app is backgrounded, so take it back on return
  // rather than waiting up to a renewal interval with a dead button.
  const onVisible = () => { if (document.visibilityState === "visible") renew(); };
  document.addEventListener("visibilitychange", onVisible);

  return () => {
    stopped = true;
    clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisible);
    try { handle?.remove?.(); } catch {}
    void p.setCapture({ on: false }).catch(() => {});
  };
}
